import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertCircle, TrendingUp, Sparkles, Pencil } from 'lucide-react'
import { Game } from '../../types/api'
import ProjectDetailsForm, { ProjectFormData } from '../editor/ProjectDetailsForm'
import { revenueService } from '../../services/revenueService'
import toast from 'react-hot-toast'

interface ProjectEditModalProps {
  isOpen: boolean
  onClose: () => void
  project: Game
  onSave: (updates: ProjectUpdateData) => Promise<void>
  code?: string // Optional - for AI generation in editor
  showGenerateButton?: boolean // Whether to show AI generate button
  screenshots?: { all: string[]; selected: number[] } // For editor screenshot management
  onScreenshotsChange?: (screenshots: { all: string[]; selected: number[] }) => void
  onCaptureScreenshot?: () => void
  isCapturingScreenshot?: boolean
}

export interface ProjectUpdateData {
  name?: string
  description?: string
  category?: string
  status?: string
  tags?: string[]
  icon_url?: string
  cover_image_url?: string
  play_instructions?: string
  screenshot_urls?: string[]
  age_rating?: string
  content_warnings?: string[]
  seo_keywords?: string[]
}

export const ProjectEditModal: React.FC<ProjectEditModalProps> = ({
  isOpen,
  onClose,
  project,
  onSave,
  code,
  showGenerateButton,
  screenshots,
  onScreenshotsChange,
  onCaptureScreenshot,
  isCapturingScreenshot
}) => {
  // Ref to access form methods
  const formRef = useRef<any>(null)
  
  // Pricing state
  const [isPaid, setIsPaid] = useState(false)
  const [price, setPrice] = useState('0.99')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  
  // Ref for auto-scrolling to pricing options
  const priceInputRef = useRef<HTMLDivElement>(null)

  const handleFormSave = async (data: ProjectFormData) => {
    try {
      console.log('💾 Saving project details:', data);
      
      // Save project details only (pricing is saved separately by the Save button)
      await onSave({
        name: data.title,
        description: data.description,
        category: data.categories[0]?.toLowerCase(), // First category as primary, lowercase for validation
        tags: data.categories, // Categories as tags array
        icon_url: data.iconUrl,
        cover_image_url: data.bannerUrl,
        play_instructions: data.playInstructions,
        screenshot_urls: data.screenshots || []
      })
    } catch (error) {
      console.error('Failed to save project details:', error)
      throw error
    }
  }

  const validatePrice = (priceStr: string): boolean => {
    const priceNum = parseFloat(priceStr)
    
    if (isNaN(priceNum)) {
      setError('Please enter a valid number')
      return false
    }
    
    if (priceNum < 0.09) {
      setError('Minimum price is $0.09')
      return false
    }
    
    setError(null)
    return true
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPrice(value)
    
    if (isPaid && value) {
      validatePrice(value)
    } else {
      setError(null)
    }
  }

  const priceNum = parseFloat(price) || 0
  const platformFee = priceNum * 0.30
  const creatorEarnings = priceNum * 0.70

  // Load current pricing when modal opens
  useEffect(() => {
    if (isOpen && project.id) {
      const loadPricing = async () => {
        try {
          const pricing = await revenueService.getGamePricing(project.id)
          if (pricing) {
            setIsPaid(pricing.is_paid)
            setPrice(pricing.price.toString())
          }
        } catch (error) {
          console.log('No pricing set yet or error loading:', error)
        }
      }
      loadPricing()
    }
  }, [isOpen, project.id])

  // Auto-scroll to price input when paid game is selected
  useEffect(() => {
    if (isPaid && priceInputRef.current) {
      setTimeout(() => {
        priceInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
  }, [isPaid])

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Embedded ProjectDetailsForm and Pricing */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            <ProjectDetailsForm
              ref={formRef}
              projectId={project.id}
              code={code} // Pass code for AI generation
              initialData={{
                title: project.name,
                categories: project.content?.tags || [],
                description: project.content?.description || '',
                playInstructions: project.content?.play_instructions || '',
                iconUrl: project.content?.icon_url,
                bannerUrl: project.content?.cover_image_url,
                screenshots: project.content?.screenshot_urls || []
              }}
              onSave={handleFormSave}
              onClose={onClose}
              showGenerateButton={showGenerateButton ?? project.is_editable_in_studio ?? false} // Show for studio projects
              isEditableInStudio={project.is_editable_in_studio ?? false} // Pass project type flag
              hideButtons={true} // Hide form buttons, we'll render them in modal footer
              screenshots={screenshots}
              onScreenshotsChange={onScreenshotsChange}
              onCaptureScreenshot={onCaptureScreenshot}
              isCapturingScreenshot={isCapturingScreenshot}
            />

            {/* Pricing Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="px-6 py-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Game Pricing</h3>
              
              <div className="space-y-4">
                {/* Pricing Type Selection */}
                <div className="space-y-3">
                  {/* Free Option */}
                  <label className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all ${
                    !isPaid 
                      ? 'border-2 border-primary-500 bg-primary-50 dark:bg-primary-500/10' 
                      : 'border border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700'
                  }`}
                  >
                    <input
                      type="radio"
                      name="pricing-type"
                      checked={!isPaid}
                      onChange={() => {
                        setIsPaid(false)
                        setError(null)
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white">Free Game</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Anyone can play your game for free
                      </div>
                    </div>
                  </label>

                  {/* Paid Option */}
                  <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isPaid 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700'
                  }`}
                  >
                    <input
                      type="radio"
                      name="pricing-type"
                      checked={isPaid}
                      onChange={() => setIsPaid(true)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white">Paid Game</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Users must purchase your game to play
                      </div>
                    </div>
                  </label>
                </div>

                {/* Price Input (only shown when paid) */}
                {isPaid && (
                  <>
                    <div ref={priceInputRef}>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Price
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1 max-w-xs">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-semibold">
                            $
                          </span>
                          <input
                            type="number"
                            value={price}
                            onChange={handlePriceChange}
                            step="0.01"
                            min="0.09"
                            max="9999.99"
                            className={`
                              w-full pl-8 pr-4 py-2 border-2 rounded-lg
                              text-gray-900 dark:text-white
                              bg-white dark:bg-gray-800
                              focus:outline-none focus:ring-2 focus:ring-primary-500
                              ${error 
                                ? 'border-red-500 dark:border-red-500' 
                                : 'border-gray-300 dark:border-gray-600'
                              }
                            `}
                            placeholder="0.99"
                          />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">USD</span>
                      </div>
                      
                      {/* Validation Message */}
                      {error && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-red-600 dark:text-red-400">
                          <AlertCircle className="w-4 h-4" />
                          {error}
                        </div>
                      )}
                      
                      {/* Price Range Info */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Minimum price is $0.09
                      </p>
                    </div>

                    {/* Revenue Split Calculator */}
                    {priceNum >= 0.09 && !error && (
                      <div className="bg-primary-50 dark:bg-primary-500/10 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                            Revenue Split
                          </h4>
                        </div>
                        
                        <div className="space-y-2">
                          {/* Creator Earnings */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-700 dark:text-gray-300">Your earnings (70%)</span>
                            <span className="text-base font-bold text-primary-600 dark:text-primary-400">
                              ${creatorEarnings.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Platform Fee */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 dark:text-gray-400">Platform fee (30%)</span>
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                              ${platformFee.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            </div>
          </div>

          {/* Save Button Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4">
              <div className="flex justify-end gap-3">
                {/* Generate Button (only for studio projects) */}
                {showGenerateButton && (
                  <button
                    onClick={async () => {
                      setIsGenerating(true)
                      try {
                        if (formRef.current?.handleGenerate) {
                          await formRef.current.handleGenerate()
                        }
                      } catch (error) {
                        console.error('Generate failed:', error)
                      } finally {
                        setIsGenerating(false)
                      }
                    }}
                    disabled={isGenerating || isSaving}
                    className={`px-6 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2 border ${
                      isGenerating || isSaving
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-700'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Sparkles className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                    {isGenerating ? 'Generating...' : 'Generate'}
                  </button>
                )}
                
                {/* Save Button */}
                <button
                  onClick={async () => {
                    if (isPaid && !validatePrice(price)) {
                      toast.error('Please fix pricing errors before saving')
                      return
                    }
                    
                    setIsSaving(true)
                    try {
                      // Save project details first
                      if (formRef.current?.save) {
                        await formRef.current.save()
                      }
                      
                      // Then save pricing
                      const priceNum = isPaid ? parseFloat(price) : 0
                      console.log('💰 Saving pricing:', { gameId: project.id, isPaid, priceNum })
                      await revenueService.setGamePricing(project.id, isPaid, priceNum)
                      
                      toast.success('Project and pricing saved successfully')
                    } catch (error: any) {
                      console.error('Save failed:', error)
                      toast.error(error.message || 'Failed to save')
                    } finally {
                      setIsSaving(false)
                    }
                  }}
                  disabled={isSaving || isGenerating || (isPaid && !!error)}
                  className={`px-6 py-2 font-semibold rounded-lg transition-colors ${
                    isSaving || isGenerating || (isPaid && error)
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-700'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={isSaving || isGenerating || (isPaid && error) ? {} : { background: 'linear-gradient(to right, #FCB902, #FF00CF)' }}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default ProjectEditModal
